// SPDX-License-Identifier: AGPL-3.0-or-later

import {Routes} from '@app/app/Routes';
import type {Channel} from '@app/features/channel/models/Channel';
import * as RouterUtils from '@app/features/navigation/utils/RouterUtils';
import {http} from '@app/features/platform/transport/RestTransport';
import {createTask, loadGuildTasks} from '@app/features/tasks/commands/TaskCommands';
import styles from '@app/features/tasks/components/ChatDetailsPanel.module.css';
import {TaskRow} from '@app/features/tasks/components/TasksView';
import ChatDetailsPanelState from '@app/features/tasks/state/ChatDetailsPanel';
import Tasks from '@app/features/tasks/state/Tasks';
import {
	ADD_TODO_HERE_DESCRIPTOR,
	FILES_DESCRIPTOR,
	FILES_INDEXING_DESCRIPTOR,
	HIDE_DONE_DESCRIPTOR,
	NO_CHAT_FILES_DESCRIPTOR,
	NO_CHAT_TODOS_DESCRIPTOR,
	SHOW_DONE_DESCRIPTOR,
	TODOS_DESCRIPTOR,
} from '@app/features/tasks/utils/TaskMessageDescriptors';
import {useLingui} from '@lingui/react/macro';
import {DownloadSimpleIcon} from '@phosphor-icons/react';
import clsx from 'clsx';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useEffect, useState} from 'react';

interface SharedFile {
	id: string;
	messageId: string;
	filename: string;
	contentType: string | null;
	size: number;
	url: string | null;
	previewUrl: string | null;
	author: string;
	timestamp: string;
}

interface SearchAttachment {
	id: string;
	filename: string;
	content_type?: string | null;
	size: number;
	url?: string | null;
	proxy_url?: string | null;
	width?: number | null;
}

interface SearchMessage {
	id: string;
	timestamp: string;
	author: {username: string; global_name?: string | null};
	attachments?: Array<SearchAttachment> | null;
}

function formatSize(bytes: number): string {
	if (bytes < 1024) return `${bytes} B`;
	if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
	return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function extensionOf(filename: string): string {
	const dot = filename.lastIndexOf('.');
	return dot > 0 ? filename.slice(dot + 1, dot + 5).toUpperCase() : 'FILE';
}

function tileKind(file: SharedFile): 'pdf' | 'sheet' | 'image' | 'other' {
	const ext = extensionOf(file.filename);
	if (file.contentType?.startsWith('image/')) return 'image';
	if (ext === 'PDF') return 'pdf';
	if (['XLS', 'XLSX', 'CSV', 'NUMB', 'ODS'].includes(ext)) return 'sheet';
	return 'other';
}

async function fetchChannelFiles(channelId: string): Promise<{files: Array<SharedFile>; indexing: boolean}> {
	const response = await http.post<{messages?: Array<SearchMessage>; indexing?: boolean}>('/search/messages', {
		body: {channel_id: [channelId], context_channel_id: channelId, has: ['file'], scope: 'current'},
	});
	const body = response.body;
	if (!body || body.indexing) return {files: [], indexing: true};
	const files: Array<SharedFile> = [];
	for (const message of body.messages ?? []) {
		for (const attachment of message.attachments ?? []) {
			const isImage = attachment.content_type?.startsWith('image/') ?? false;
			const proxy = attachment.proxy_url ?? attachment.url ?? null;
			files.push({
				id: attachment.id,
				messageId: message.id,
				filename: attachment.filename,
				contentType: attachment.content_type ?? null,
				size: attachment.size,
				url: attachment.url ?? proxy,
				previewUrl: isImage && proxy ? `${proxy}${proxy.includes('?') ? '&' : '?'}width=80&height=80` : null,
				author: message.author.global_name || message.author.username,
				timestamp: message.timestamp,
			});
		}
	}
	return {files, indexing: false};
}

const FilesTab: React.FC<{channel: Channel}> = ({channel}) => {
	const {i18n} = useLingui();
	const [state, setState] = useState<{files: Array<SharedFile>; indexing: boolean} | null>(null);
	useEffect(() => {
		let cancelled = false;
		setState(null);
		fetchChannelFiles(channel.id)
			.then((result) => {
				if (!cancelled) setState(result);
			})
			.catch(() => {
				if (!cancelled) setState({files: [], indexing: false});
			});
		return () => {
			cancelled = true;
		};
	}, [channel.id]);
	if (!state) return null;
	if (state.indexing)
		return (
			<p className={styles.empty} data-flx="tasks.chat-details-panel.files-tab.indexing">
				{i18n._(FILES_INDEXING_DESCRIPTOR)}
			</p>
		);
	if (state.files.length === 0)
		return (
			<p className={styles.empty} data-flx="tasks.chat-details-panel.files-tab.empty">
				{i18n._(NO_CHAT_FILES_DESCRIPTOR)}
			</p>
		);
	return (
		<div className={styles.files} data-flx="tasks.chat-details-panel.files-tab.files">
			{state.files.map((file) => {
				const kind = tileKind(file);
				const date = new Date(file.timestamp).toLocaleDateString(i18n.locale, {month: 'short', day: 'numeric'});
				return (
					<div key={file.id} className={styles.file} data-flx="tasks.chat-details-panel.files-tab.file">
						<button
							type="button"
							className={styles.fileMain}
							onClick={() =>
								channel.guildId &&
								RouterUtils.transitionTo(Routes.channelMessage(channel.guildId, channel.id, file.messageId))
							}
							data-flx="tasks.chat-details-panel.files-tab.file-open"
						>
							{file.previewUrl ? (
								<img
									src={file.previewUrl}
									alt=""
									className={styles.thumb}
									loading="lazy"
									data-flx="tasks.chat-details-panel.files-tab.thumb"
								/>
							) : (
								<span
									className={clsx(styles.tile, styles[`tile_${kind}`])}
									data-flx="tasks.chat-details-panel.files-tab.tile"
								>
									{extensionOf(file.filename)}
								</span>
							)}
							<span className={styles.fileText} data-flx="tasks.chat-details-panel.files-tab.file-text">
								<span className={styles.fileName} data-flx="tasks.chat-details-panel.files-tab.file-name">
									{file.filename}
								</span>
								<span className={styles.fileMeta} data-flx="tasks.chat-details-panel.files-tab.file-meta">
									{file.author} · {date} · {formatSize(file.size)}
								</span>
							</span>
						</button>
						{file.url && (
							<a
								href={file.url}
								download={file.filename}
								className={styles.download}
								aria-label={file.filename}
								target="_blank"
								rel="noreferrer"
								data-flx="tasks.chat-details-panel.files-tab.download"
							>
								<DownloadSimpleIcon size={16} data-flx="tasks.chat-details-panel.files-tab.download-icon" />
							</a>
						)}
					</div>
				);
			})}
		</div>
	);
};

const TodosTab: React.FC<{channel: Channel}> = observer(({channel}) => {
	const {i18n} = useLingui();
	const guildId = channel.guildId ?? '';
	const [draft, setDraft] = useState('');
	const [showDone, setShowDone] = useState(false);
	useEffect(() => {
		if (guildId) void loadGuildTasks(guildId);
	}, [guildId]);
	const all = guildId ? Tasks.getChannelTasks(guildId, channel.id) : [];
	const open = all.filter((task) => task.status !== 'done');
	const done = all.filter((task) => task.status === 'done');
	const add = async () => {
		const title = draft.trim();
		if (!title || !guildId) return;
		const created = await createTask(guildId, {title, channel_id: channel.id});
		if (created) setDraft('');
	};
	return (
		<div className={styles.todos} data-flx="tasks.chat-details-panel.todos-tab.todos">
			<input
				type="text"
				className={styles.add}
				value={draft}
				placeholder={i18n._(ADD_TODO_HERE_DESCRIPTOR)}
				aria-label={i18n._(ADD_TODO_HERE_DESCRIPTOR)}
				onChange={(event) => setDraft(event.target.value)}
				onKeyDown={(event) => {
					if (event.key === 'Enter') {
						event.preventDefault();
						void add();
					}
				}}
				data-flx="tasks.chat-details-panel.todos-tab.add"
			/>
			{open.length === 0 && done.length === 0 ? (
				<p className={styles.empty} data-flx="tasks.chat-details-panel.todos-tab.empty">
					{i18n._(NO_CHAT_TODOS_DESCRIPTOR)}
				</p>
			) : (
				open.map((task) => (
					<TaskRow
						key={task.id}
						task={task}
						query=""
						showSource={false}
						data-flx="tasks.chat-details-panel.todos-tab.row"
					/>
				))
			)}
			{done.length > 0 && (
				<>
					<button
						type="button"
						className={styles.more}
						onClick={() => setShowDone((v) => !v)}
						data-flx="tasks.chat-details-panel.todos-tab.toggle-done"
					>
						{showDone ? i18n._(HIDE_DONE_DESCRIPTOR) : i18n._(SHOW_DONE_DESCRIPTOR, {count: done.length})}
					</button>
					{showDone &&
						done.map((task) => (
							<TaskRow
								key={task.id}
								task={task}
								query=""
								showSource={false}
								data-flx="tasks.chat-details-panel.todos-tab.done-row"
							/>
						))}
				</>
			)}
		</div>
	);
});

export const ChatDetailsPanel: React.FC<{channel: Channel}> = observer(({channel}) => {
	const {i18n} = useLingui();
	const tab = ChatDetailsPanelState.tab;
	const openCount = channel.guildId
		? Tasks.getChannelTasks(channel.guildId, channel.id).filter((task) => task.status !== 'done').length
		: 0;
	return (
		<aside className={styles.panel} data-flx="tasks.chat-details-panel.panel">
			<div className={styles.tabs} role="tablist" data-flx="tasks.chat-details-panel.tabs">
				<button
					type="button"
					role="tab"
					aria-selected={tab === 'todos'}
					className={clsx(styles.tab, tab === 'todos' && styles.tabOn)}
					onClick={() => ChatDetailsPanelState.setTab('todos')}
					data-flx="tasks.chat-details-panel.tab-todos"
				>
					{i18n._(TODOS_DESCRIPTOR)}
					<span className={styles.count} data-flx="tasks.chat-details-panel.tab-todos-count">
						{openCount}
					</span>
				</button>
				<button
					type="button"
					role="tab"
					aria-selected={tab === 'files'}
					className={clsx(styles.tab, tab === 'files' && styles.tabOn)}
					onClick={() => ChatDetailsPanelState.setTab('files')}
					data-flx="tasks.chat-details-panel.tab-files"
				>
					{i18n._(FILES_DESCRIPTOR)}
				</button>
			</div>
			<div className={styles.body} data-flx="tasks.chat-details-panel.body">
				{tab === 'todos' ? (
					<TodosTab channel={channel} data-flx="tasks.chat-details-panel.todos-tab" />
				) : (
					<FilesTab channel={channel} data-flx="tasks.chat-details-panel.files-tab" />
				)}
			</div>
		</aside>
	);
});
